import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Heart, Baby, Home, Users, Clock, Shield } from 'lucide-react';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const services = [
    {
      title: "Accompagnement et suivi de grossesse",
      description: "Accompagnement complet pendant votre grossesse avec consultations personnalisées, surveillance médicale et préparation à la naissance.",
      icon: Heart,
      image: "/img/services/service-grossesse.jpg",
      features: [
        "Consultations mensuelles",
        "Préparation à la naissance physiologique",
        "Sensibilisation aux perturbateurs environnementaux",
        "Disponible 24h/24 – 7j/7 (37SA-41SA+6 jours)"
      ]
    },
    {
      title: "Accouchement en plateau technique",
      description: "Accouchement physiologique (sans péridurale) au sein d’une maternité sécurisée, avec la même sage-femme du suivi.",
      icon: Home,
      image: "/img/services/service-accouchement.jpg",
      features: [
        "Continuité de l'accompagement",
        "Environnement sécurisant",
        "Retour à domicile en 3h possible",
        "Visites quotidiennes à domicile"
      ]
    },
    {
      title: "Suivi et prise en charge postnatale",
      description: "Suivi attentif après la naissance pour favoriser votre récupération et l'adaptation du nouveau-né au retour au domicile.",
      icon: Baby,
      image: "/img/services/service-postpartum.jpg",
      features: [
        "Surveillance maman-bébé",
        "Accompagnement allaitement",
        "Surveillance de l’ictère",
        "Soutien émotionnel"
      ]
    }
  ];

  const additionalServices = [
    { icon: Users, title: "Préparation à la naissance (physiologique)", description: "Sessions par groupe" },
    { icon: Clock, title: "Disponibilité 24h/7j (37 SA → 41SA+6 jours)", description: "Pour l’accompagnement plateau technique" },
    { icon: Shield, title: "Sécurité médicale", description: "Protocoles respectés, orientation si besoin" }
  ];

  return (
    <section id="services" className="py-20 lg:py-32 bg-gradient-to-br from-warm-50 to-sage-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 lg:mb-20"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-4xl lg:text-5xl font-bold text-sage-800 mb-6"
          >
            Services d'accompagnement
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl text-sage-600 max-w-3xl mx-auto leading-relaxed"
          >
            Un accompagnement complet et personnalisé pour chaque étape de votre maternité, 
            du début de la grossesse aux premiers mois avec votre bébé.
          </motion.p>
        </motion.div>

        {/* Main Services */}
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 mb-20">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 + index * 0.2 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group flex flex-col"
              >
                <div className="glass-card rounded-3xl overflow-hidden h-full shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col">
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-sage-900/60 via-sage-800/20 to-transparent z-10"></div>
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-6 left-6 z-20">
                      <div className="bg-white/20 backdrop-blur-md p-3 rounded-full">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 flex flex-col flex-grow">
                    <h3 className="font-display text-2xl font-semibold text-sage-800 mb-4 text-center">
                      {service.title}
                    </h3>
                    <p className="text-sage-600 mb-6 leading-relaxed text-center">
                      {service.description}
                    </p>

                    {/* Features */}
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="relative px-6 text-center text-sage-700">
                          <div className="absolute left-0 top-[0.5rem] w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                          <span className="text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* "C’est quoi ?" - Accouchement en plateau technique */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="max-w-4xl mx-auto mb-20"
        >
          <div className="glass-card p-8 rounded-3xl">
            <h3 className="font-display text-2xl font-semibold text-sage-800 mb-4">
              Accouchement en plateau technique — C’est quoi ?
            </h3>
            <p className="text-sage-700 leading-relaxed">
              
              « L’accompagnement en plateau technique vous permet de bénéficier d’un suivi personnalisé par une équipe de sages-femmes tout au long de votre grossesse : suivi prénatal, séances de préparation à la naissance et rencontres régulières avec les différentes sages-femmes de l’équipe.
<br />
Le jour de votre accouchement, c’est la sage-femme de l’équipe qui est d’astreinte ce jour-là qui vous accompagne. Vous l’aurez déjà rencontrée à plusieurs reprises pendant votre grossesse et elle est formée et expérimentée dans l’accompagnement des accouchements physiologiques.
<br />
Lorsque le travail commence, elle vous rejoint à la maternité pour vous accompagner dans votre projet de naissance physiologique, sans péridurale, tout en bénéficiant de la sécurité d’un environnement hospitalier.
<br />
Dans mon cas, les accouchements ont lieu au Centre Hospitalier Annecy Genevois, au sein de sa salle de naissance physiologique. Vous pouvez d’ailleurs découvrir les lieux grâce à la visite virtuelle.
<br />
Après la naissance, si vous et votre bébé allez bien, vous pouvez rentrer à votre domicile environ 3 heures après l’accouchement. La sage-femme poursuit alors votre accompagnement à domicile, avec une visite quotidienne pour vous soutenir dans les premiers jours avec votre bébé.
<br />
Et si une complication survient à n’importe quel moment, l’équipe de la maternité prend immédiatement le relais et vous bénéficiez de l’ensemble des soins et de la sécurité du cadre hospitalier.
<br />
L’objectif : vous offrir un accompagnement personnalisé, dans la continuité de votre grossesse, pour vivre une naissance aussi physiologique que possible, tout en restant dans un environnement sécurisé.»
            </p>
          </div>
        </motion.div>

        {/* Additional Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="grid md:grid-cols-3 gap-8"
        >
          {additionalServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 1.4 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="text-center p-6 rounded-2xl hover:bg-white/50 transition-all duration-300 group flex flex-col h-full"
              >
                <div className="flex justify-center mb-6">
                  <div className="bg-primary-100 group-hover:bg-primary-200 p-4 rounded-full transition-colors duration-300">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                </div>
                <h4 className="font-display text-xl font-semibold text-sage-800 mb-3 flex-grow">
                  {service.title}
                </h4>
                <p className="text-sage-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
